/**
 * v3.fraud — WAVE-11 Fraud Case intake client.
 *
 * Backed by Functions/FraudFunction.cs. Persists to dbo.FraudCases +
 * dbo.FraudTransactions. Per Bill 2026-04-26: v3 OWNS the storage; legacy
 * Canvas app + Dataverse-bridge cloud flow are NOT touched by this client.
 */

import type {
  RequestOptions,
  FraudCaseSubmitInput,
  FraudCaseSubmitResponse,
  FraudCaseDetail,
  FraudCaseListItem,
  FraudCatalogEntry,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface FraudCaseDetailResponse {
  success: boolean;
  fraudCase?: FraudCaseDetail;
  error?: string;
}

export interface FraudCaseListResponse {
  success: boolean;
  count: number;
  items: ReadonlyArray<FraudCaseListItem>;
}

export interface FraudCatalogResponse {
  success: boolean;
  count: number;
  items: ReadonlyArray<FraudCatalogEntry>;
}

export interface FraudCatalogUpsertInput {
  code: string;
  description: string;
  isActive?: boolean;
  sortOrder?: number;
}

export interface FraudAdminUpsertResponse {
  success: boolean;
  error?: string;
}

export class FraudApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public submit(body: FraudCaseSubmitInput, opts?: RequestOptions): Promise<FraudCaseSubmitResponse> {
    return this.http.post<FraudCaseSubmitResponse>('/v3/fraud-cases', { ...opts, body });
  }

  public get(caseId: string, opts?: RequestOptions): Promise<FraudCaseDetailResponse> {
    return this.http.get<FraudCaseDetailResponse>(`/v3/fraud-cases/${encodeURIComponent(caseId)}`, { ...opts });
  }

  public listMine(filters: { upn?: string; top?: number } = {}, opts?: RequestOptions): Promise<FraudCaseListResponse> {
    return this.http.get<FraudCaseListResponse>('/v3/fraud-cases/me', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public mcc(opts?: RequestOptions): Promise<FraudCatalogResponse> {
    return this.http.get<FraudCatalogResponse>('/v3/config/fraud-mcc', { ...opts });
  }

  public pos(opts?: RequestOptions): Promise<FraudCatalogResponse> {
    return this.http.get<FraudCatalogResponse>('/v3/config/fraud-pos', { ...opts });
  }

  public caseTypes(opts?: RequestOptions): Promise<FraudCatalogResponse> {
    return this.http.get<FraudCatalogResponse>('/v3/config/fraud-case-types', { ...opts });
  }

  public disputeReasons(opts?: RequestOptions): Promise<FraudCatalogResponse> {
    return this.http.get<FraudCatalogResponse>('/v3/config/fraud-dispute-reasons', { ...opts });
  }

  public fraudTypes(opts?: RequestOptions): Promise<FraudCatalogResponse> {
    return this.http.get<FraudCatalogResponse>('/v3/config/fraud-fraud-types', { ...opts });
  }

  public adminMccUpsert(
    bypassKey: string,
    body: FraudCatalogUpsertInput,
    opts?: RequestOptions,
  ): Promise<FraudAdminUpsertResponse> {
    const headers = { ...(opts?.headers ?? {}), 'x-config-seed-bypass': bypassKey };
    return this.http.post<FraudAdminUpsertResponse>('/v3/admin/fraud-mcc', { ...opts, headers, body });
  }

  public adminPosUpsert(
    bypassKey: string,
    body: FraudCatalogUpsertInput,
    opts?: RequestOptions,
  ): Promise<FraudAdminUpsertResponse> {
    const headers = { ...(opts?.headers ?? {}), 'x-config-seed-bypass': bypassKey };
    return this.http.post<FraudAdminUpsertResponse>('/v3/admin/fraud-pos', { ...opts, headers, body });
  }

  // ── Dataverse-bridge wrappers ──────────────────────────────────────────
  // Wave-11 bridge endpoints that proxy from v3 → the legacy Dataverse
  // FraudCase table. Used while the Canvas app + cloud flow still own the
  // closing-action data; the wrappers stay stable when the bridge retires.

  /**
   * List the current user's submitted fraud cases via the Dataverse bridge.
   * Used by the My Fraud Cases page until v3 owns the read path end-to-end.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public listMineDataverse(filters: Record<string, unknown> = {}, opts?: RequestOptions): Promise<any> {
    return this.http.get('/v3/fraud-cases/me-dataverse', {
      ...opts,
      query: filters as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Create a fraud case via the Dataverse bridge. Server-side adapter
   * normalizes the body, writes to v3 SQL, and replays into Dataverse
   * for the Canvas app to pick up the case.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createDataverse(body: unknown, opts?: RequestOptions): Promise<any> {
    return this.http.post('/v3/fraud-cases/dataverse', { ...opts, body });
  }

  /**
   * AcctHist — list transaction history for the picker in
   * SubmitFraudCaseWizard. Mirrors the JHA AcctHistSrch shape but is
   * served through the v3 Function so authorization stays centralized.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public acctHist(body: unknown, opts?: RequestOptions): Promise<any> {
    return this.http.post('/v3/fraud-cases/acct-hist', { ...opts, body });
  }
}
